import dayjs from "dayjs";
import { FC } from "react";
import { capitalize } from "~/utils"; // Removed getClientName if redundant
import { useDetailBookingStore } from "./detail-booking.store";

type Props = {};

const getStatusBadge = (status: string) => {
  let classes = "px-2 py-1 rounded-full text-xs font-medium ";

  switch (status.toLowerCase()) {
    case "completed":
      classes += "bg-green-100 text-green-800";
      break;
    case "in-progress":
      classes += "bg-blue-100 text-blue-800";
      break;
    case "scheduled":
      classes += "bg-purple-100 text-purple-800";
      break;
    case "estimated":
      classes += "bg-yellow-100 text-yellow-800";
      break;
    case "cancelled":
      classes += "bg-red-100 text-red-800";
      break;
    case "draft":
      classes += "bg-gray-100 text-gray-800";
      break;
    default:
      classes += "bg-gray-100 text-gray-800";
  }

  return (
    <span className={classes}>
      {status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ")}
    </span>
  );
};

const getPaymentBadge = (status?: string) => {
  let classes = "px-2 py-1 rounded-full text-xs font-medium ";

  switch (status?.toLowerCase()) {
    case "paid":
      classes += "bg-green-100 text-green-800";
      break;
    case "partial":
      classes += "bg-blue-100 text-blue-800";
      break;
    case "pending":
      classes += "bg-yellow-100 text-yellow-800";
      break;
    case "overdue":
      classes += "bg-red-100 text-red-800";
      break;
    default:
      classes += "bg-gray-100 text-gray-800";
  }

  return (
    <span className={classes}>
      {capitalize(status ?? "unknown").replace("-", " ")}
    </span>
  );
};

export const QuickInfo: FC<Props> = () => {
  const { booking } = useDetailBookingStore();

  if (!booking) {
    return <div className="text-gray-500">No booking data available</div>;
  }

  const clientName =
    booking.client?.companyName ||
    [booking.client?.firstName, booking.client?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    booking.tempClientData?.name ||
    "Unnamed Client";

  const isAppBooking = booking.serviceTicket?.some((ticket: any) => 
    ticket.staffNotes && ticket.staffNotes.includes("=== CLIENT SERVICE REQUEST ===")
  );

  return (
    <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <div className="text-sm text-gray-500 mb-1">Client</div>
          <div className="text-sm">
            {clientName}
            {booking.tempClientData && !booking.client && (
              <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">New Lead</span>
            )}
            {isAppBooking && (
              <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">APP BOOKING</span>
            )}
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-500 mb-1">Project</div>
          <div className="text-sm font-medium">{booking.project?.name || "-"}</div>
          {booking.sidemark && (
            <div className="text-xs text-gray-500 mt-1">
              Sidemark: <span className="font-mono">{booking.sidemark}</span>
            </div>
          )}
        </div>

        <div>
          <div className="text-sm text-gray-500 mb-1">Status</div>
          <div className="flex items-center space-x-2">
            {getStatusBadge(booking.status ?? "unknown")}
            <span className="text-sm text-gray-600">
              Since {new Date(booking.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-500 mb-1">Services</div>
          <div className="flex flex-wrap gap-1">
            {(() => {
              // Count service types
              const serviceTypeCounts = booking.serviceTicket?.reduce((acc: Record<string, number>, service: any) => {
                const type = service.serviceType;
                acc[type] = (acc[type] || 0) + 1;
                return acc;
              }, {} as Record<string, number>);

              // Create badges with counts
              return Object.entries(serviceTypeCounts || {}).map(([type, count]) => (
                <span
                  key={type}
                  className="px-2 py-1 rounded-full text-xs bg-gray-100"
                >
                  {count} {capitalize(type)}
                </span>
              ));
            })()}
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-500 mb-1">Estimate</div>
          {booking.invoiceConfig?.grandTotal ? (
            <div className="flex items-center space-x-2">
              <span className="text-lg font-semibold">
                ${booking.invoiceConfig.grandTotal.toFixed(2)}
              </span>
            </div>
          ) : (
            <div className="text-sm text-gray-500">No estimate</div>
          )}
          {booking.payment && (
            <div className="mt-1">
              <div className="flex items-center space-x-2">
                {getPaymentBadge(booking.payment?.status)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {booking.payment?.status === "paid"
                  ? "Paid in full"
                  : booking.payment?.due && booking.payment?.dueDate
                    ? `$${booking.payment?.due?.toFixed(2)} due on ${dayjs(
                      booking.payment?.dueDate
                    ).format("D/M/YYYY")}`
                    : "-"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
